import { createContext, useContext, useEffect, useMemo, useCallback } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from '@tanstack/react-query'
import type { User } from '@/types/user'
import type { Studio } from '@/types/studio'
import type { UserSession } from '@/types/permissions'
import { mapAuth0UserToUser, getPrimaryStudioId } from './auth0-mapper'
import type { Auth0UserWithClaims } from './auth0-mapper'
import { getStudioById } from '@/data/studios'
import { logger } from '@/lib/logger'
import { setUserContext, clearUserContext } from '@/lib/error-tracking'
import { env } from '@/config/env'
import { useRbacMe } from '@/lib/hooks/use-rbac-me'

export interface AuthContextValue {
  user: User | null
  studio: Studio | null
  isAuthenticated: boolean
  isLoading: boolean
  loginWithGoogle: () => Promise<void>
  logout: () => void
  /**
   * Get access token for API calls
   * @returns Access token or undefined if not authenticated
   */
  getAccessToken: () => Promise<string | undefined>

  /**
   * User session with studio context and permissions
   * This is the new API for permission-aware components
   */
  session: UserSession | null

  /**
   * Whether the session is currently loading
   */
  isSessionLoading: boolean

  /**
   * Refetch the session from the backend
   */
  refetchSession: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

/**
 * Hook to access auth context
 */
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}


/**
 * Auth provider component that wraps Auth0
 * Maintains backward compatibility with existing interface
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const {
    user: auth0User,
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0IsLoading,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0()

  // Map Auth0 user to AerialFlow User type
  // In development, override the user's roles if DEV_ROLE_OVERRIDE is set
  const user = useMemo(() => {
    if (!auth0User || !auth0IsAuthenticated) return null
    const mappedUser = mapAuth0UserToUser(auth0User as Auth0UserWithClaims)

    // Apply dev role override to user roles
    if (env.IS_DEV && env.DEV_ROLE_OVERRIDE) {
      return {
        ...mappedUser,
        roles: [env.DEV_ROLE_OVERRIDE],
      }
    }

    return mappedUser
  }, [auth0User, auth0IsAuthenticated])

  // Extract primary studio ID from Auth0 claims
  const primaryStudioId = useMemo(() => {
    if (!auth0User) return null
    return getPrimaryStudioId(auth0User as Auth0UserWithClaims)
  }, [auth0User])

  // Get access token callback
  const getAccessToken = useCallback(async (): Promise<string | undefined> => {
    try {
      return await getAccessTokenSilently()
    } catch (error) {
      logger.error('Failed to get access token', error)
      return undefined
    }
  }, [getAccessTokenSilently])

  // Fetch session with permissions from backend using the reusable hook
  // Uses aggressive caching (15 min staleTime) to reduce API calls
  const {
    session,
    isLoading: sessionLoading,
    refetch: refetchSession,
  } = useRbacMe()

  // Fetch studio data when user is authenticated (legacy support)
  const {
    data: studio,
    isLoading: studioLoading,
  } = useQuery({
    queryKey: ['studio', primaryStudioId],
    queryFn: async () => {
      if (!primaryStudioId) {
        // Default to first studio for MVP if no primary studio set
        return getStudioById('studio-1')
      }
      return getStudioById(primaryStudioId)
    },
    enabled: auth0IsAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Set error tracking context when user changes
  useEffect(() => {
    if (user) {
      setUserContext({
        id: user.id,
        email: user.email,
        name: user.name,
        roles: user.roles,
      })
    } else {
      clearUserContext()
    }
  }, [user])

  /**
   * Initiate Google OAuth login via Auth0
   */
  const loginWithGoogle = useCallback(async () => {
    await loginWithRedirect({
      authorizationParams: {
        connection: 'google-oauth2',
      },
    })
  }, [loginWithRedirect])

  /**
   * Logout and redirect to home
   */
  const logout = useCallback(() => {
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    })
  }, [auth0Logout])

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      user,
      studio: studio ?? null,
      isAuthenticated: auth0IsAuthenticated && !!user,
      isLoading: auth0IsLoading || (auth0IsAuthenticated && studioLoading),
      loginWithGoogle,
      logout,
      getAccessToken,
      session: session ?? null,
      isSessionLoading: sessionLoading,
      refetchSession: async () => {
        await refetchSession()
      },
    }),
    [
      user,
      studio,
      auth0IsAuthenticated,
      auth0IsLoading,
      studioLoading,
      loginWithGoogle,
      logout,
      getAccessToken,
      session,
      sessionLoading,
      refetchSession,
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
